import React from "react";
import Card, { EmptyBlock } from "./Card";
import { COLORS, FONT } from "./theme";

/**
 * Tabela do dashboard — também a "table view" dos gráficos.
 *
 * Todo valor que aparece num gráfico precisa estar legível em algum lugar sem
 * depender de hover ou de distinguir cor. É esse o papel destas tabelas: o
 * gráfico é a leitura rápida, a tabela é a leitura exata.
 *
 * `columns` = [{ title, field, align, width, render }]. `render` recebe a linha.
 */
export default function RankingTable({
  title,
  hint,
  columns,
  rows,
  emptyText,
  flex = "1 1 420px",
  minWidth = 320,
  maxHeight = 300,
  action,
}) {
  return (
    <Card title={title} hint={hint} flex={flex} minWidth={minWidth} action={action}>
      {rows.length === 0 ? (
        <EmptyBlock height={140}>{emptyText}</EmptyBlock>
      ) : (
        <div style={{ maxHeight, overflowY: "auto", overflowX: "auto", margin: "0 -4px" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: FONT,
              fontSize: 13,
            }}
          >
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.field}
                    style={{
                      textAlign: column.align || "left",
                      // Cabeçalho grudento: rolar uma lista de 50 pessoas sem
                      // saber mais que coluna é qual não ajuda ninguém.
                      position: "sticky",
                      top: 0,
                      backgroundColor: COLORS.surface,
                      fontSize: 10.5,
                      fontWeight: 600,
                      letterSpacing: "0.07em",
                      textTransform: "uppercase",
                      color: COLORS.inkMuted,
                      padding: "0 8px 8px",
                      borderBottom: `1px solid ${COLORS.border}`,
                      whiteSpace: "nowrap",
                      width: column.width,
                    }}
                  >
                    {column.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.form_id || row.user_name || index}>
                  {columns.map((column) => (
                    <td
                      key={column.field}
                      title={column.render ? undefined : row[column.field]}
                      style={{
                        textAlign: column.align || "left",
                        padding: "9px 8px",
                        borderBottom:
                          index === rows.length - 1 ? "none" : `1px solid ${COLORS.grid}`,
                        color: column.align === "right" ? COLORS.ink : COLORS.inkSecondary,
                        // Colunas de número alinham verticalmente: é o caso em
                        // que algarismo de largura fixa ajuda a leitura.
                        fontVariantNumeric: column.align === "right" ? "tabular-nums" : "normal",
                        fontWeight: column.align === "right" ? 600 : 400,
                        whiteSpace: "nowrap",
                        maxWidth: column.width,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {column.render ? column.render(row) : row[column.field]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/**
 * Marcador de estado. A cor NUNCA carrega o sentido sozinha — vem sempre com o
 * texto ao lado, que é o que quem não distingue as cores realmente lê.
 */
export function StatusDot({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}>
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      {label}
    </span>
  );
}
